/**
 * i18n — lightweight translation hook
 *
 * Usage: const t = useT();
 *        t('nav.explore')  // → "Explore" or "Entdecken"
 */

import { useLanguageStore } from '@/stores/languageStore';
import { useUiTextStore, registerPageKey, type UiTextMap } from '@/stores/uiTextStore';
import { stripFormatting } from './richText';
import en from '@/locales/en';
import de from '@/locales/de';

const locales: Record<string, Record<string, string>> = { en, de };

const NO_OVERRIDES: UiTextMap = { en: {}, de: {} };

/**
 * Resolve one key against the superadmin overrides and the bundled locales.
 *
 * `fromOverride` matters: a bundled string is returned byte for byte, exactly
 * as before this feature existed. Only a string a superadmin has edited is
 * read as formatted text, so no existing label can be changed by the parser.
 */
export function resolveText(
  key: string,
  language: string,
  overrides: UiTextMap,
  fallback?: string
): { value: string; fromOverride: boolean } {
  const strings = locales[language] || en;
  const overrideForLanguage = (overrides as unknown as Record<string, Record<string, string>>)[language];

  const localOverride = overrideForLanguage?.[key];
  if (localOverride) return { value: localOverride, fromOverride: true };

  const bundled = strings[key];
  if (bundled) return { value: bundled, fromOverride: false };

  const englishOverride = overrides.en?.[key];
  if (englishOverride) return { value: englishOverride, fromOverride: true };

  return { value: en[key] || fallback || key, fromOverride: false };
}

/**
 * The stored text for a key, unformatted markers included — what the editor
 * panel shows and what <T> renders. Subscribes to language and overrides, so
 * a save shows on the page immediately.
 */
export function useRawText(key: string, fallback?: string): { value: string; fromOverride: boolean } {
  const language = useLanguageStore((s) => s.language);
  const overrides = useUiTextStore((s) => s.overrides);
  registerPageKey(key);
  return resolveText(key, language, overrides, fallback);
}

/** Non-hook form of useRawText, for code outside React components. */
export function rawText(key: string, language?: string, fallback?: string) {
  const lang = language || useLanguageStore.getState().language;
  const overrides = useUiTextStore.getState().overrides || NO_OVERRIDES;
  return resolveText(key, lang, overrides, fallback);
}

/**
 * Returns a translation function for the current language.
 * Falls back to English, then to the key itself.
 */
export function useT() {
  const language = useLanguageStore((s) => s.language);
  const overrides = useUiTextStore((s) => s.overrides);

  return (key: string, fallback?: string): string => {
    registerPageKey(key);
    const { value, fromOverride } = resolveText(key, language, overrides, fallback);
    // Edited text may carry formatting markers; this form lands in attributes
    // and browser dialogs, so it is always read as plain text.
    return fromOverride ? stripFormatting(value) : value;
  };
}

/**
 * Non-hook version for use outside React components.
 * Reads current language from the store directly.
 */
export function t(key: string, fallback?: string): string {
  const language = useLanguageStore.getState().language;
  const overrides = useUiTextStore.getState().overrides || NO_OVERRIDES;
  const { value, fromOverride } = resolveText(key, language, overrides, fallback);
  return fromOverride ? stripFormatting(value) : value;
}

/**
 * Get localized database content based on current language.
 * Priority (migration 0037 added symmetric _en columns):
 *   - DE active: prefer _de, then original, then _en
 *   - EN active: prefer _en, then original, then _de
 *
 * "Original" may be in either language depending on who created the
 * record. The _en / _de columns hold the canonical translations.
 */
export function useLocalized() {
  const language = useLanguageStore((s) => s.language);
  return (item: any, field: string): string => {
    if (!item) return '';
    const de = item[`${field}_de`];
    const en = item[`${field}_en`];
    const orig = item[field];
    if (language === 'de') {
      return (de && de.trim()) ? de : (orig || en || '');
    }
    return (en && en.trim()) ? en : (orig || de || '');
  };
}
